import React, { createRef, type SyntheticEvent, type MouseEvent, type KeyboardEvent } from 'react';
import { SkeletonItem } from '@atlaskit/side-navigation';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { matchUrlPathname } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { SETTINGS_IDS } from '../../../constants.tsx';
import type { SettingFeatures } from '../../../types/index.tsx';
import { getSpaUrlForClassicSettings } from '../../../utils/servicedesk-spa-url-classic-settings/index.tsx';
import messages from './messages.tsx';

type AccessMenuProps = {
	projectKey: string;
	features: SettingFeatures | null;
	onHandleLegacySPAClick: (
		e:
			| SyntheticEvent
			| MouseEvent<HTMLAnchorElement>
			| KeyboardEvent<HTMLAnchorElement>
			| MouseEvent<EventTarget>
			| KeyboardEvent<EventTarget>,
	) => void;
};

export const AccessMenu = ({ projectKey, features, onHandleLegacySPAClick }: AccessMenuProps) => {
	const { formatMessage } = useIntl();
	if (features == null || projectKey == null) return <SkeletonItem />;
	return (
		<>
			{features.hasPeople && (
				<MenuItem
					analytics={{ itemId: SETTINGS_IDS.PEOPLE }}
					href={`/plugins/servlet/project-config/${projectKey}/people`}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.peopleAndAccess)}
				</MenuItem>
			)}
			{features.hasPermissions && (
				<MenuItem
					analytics={{
						itemId: SETTINGS_IDS.PERMISSIONS,
					}}
					href={`/plugins/servlet/project-config/${projectKey}/permissions`}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.projectPermissions)}
				</MenuItem>
			)}
			{features.hasCustomerPermissions && (
				<MenuItem
					analytics={{ itemId: SETTINGS_IDS.CUSTOMER_PERMISSIONS }}
					href={getSpaUrlForClassicSettings('customer-permissions', projectKey)}
					onClick={__SPA__ ? undefined : onHandleLegacySPAClick}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.customerPermissions)}
				</MenuItem>
			)}
			{features.hasIssueSecurity && (
				<MenuItem
					analytics={{
						itemId: SETTINGS_IDS.ISSUE_SECURITY,
					}}
					href={`/plugins/servlet/project-config/${projectKey}/issuesecurity`}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(
						fg('jira-issue-terminology-update-m4')
							? messages.issueSecurityIssueTerminologyRefreshM4
							: messages.issueSecurity,
					)}
				</MenuItem>
			)}
		</>
	);
};
